import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Dropdown, { Option as DropdownOption } from "react-dropdown";
import "react-dropdown/style.css";

import { useFormik } from "formik";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import OrderPageService from "./Services/OrderPageService";
import { OrderData } from "./Classes/OrderData";
import TopBanner from "./TopBanner";

type CountryDisplay = "United States" | "Canada";

type FormValues = {
  email: string;
  schoolName: string;
  schoolYear: string;
  bannerName: string;

  attention: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  country: CountryDisplay | "";
  phone: string; 
};

const PUBLIC_PATH = process.env.REACT_APP_PublicPath ?? "";

const onlyDigits = (v: string) => v.replace(/\D/g, "");
const isUS = (c: CountryDisplay | "") => c !== "Canada";

const julyMonthIndex = 6; // a school year runs July through June
const schoolYearsAhead = 2;
const schoolYearsBack = 10; // far enough back to reorder a historical banner

// Newest first, so the older years, which are ordered least often, sit at the bottom.
const getSchoolYearOptions = (): string[] => {
  const today = new Date();
  const currentStartYear =
    today.getMonth() >= julyMonthIndex ? today.getFullYear() : today.getFullYear() - 1;
  const years: string[] = [];
  for (let offset = schoolYearsAhead; offset >= -schoolYearsBack; offset--) {
    const startYear = currentStartYear + offset;
    years.push(startYear + "-" + (startYear + 1));
  }
  return years;
};

export default function Order(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [editOrderId, setEditOrderId] = useState<string>("");
  const [userName, setUserName] = useState<string>("");
  const [isCountryUS, setIsCountryUS] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  const bannerOptions = useMemo<string[]>(
    () => ["Elementary", "Elementary - Lighthouse", "Secondary", "Secondary - Lighthouse", "Legacy"],
    []
  );

  const stateOptions = useMemo<string[]>(
    () => [
      "AL","AK","AZ","AR","CA","CO","CT","DC","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
      "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD",
      "TN","TX","UT","VT","VA","WA","WV","WI","WY",
    ],
    []
  );

  const countryOptions = useMemo<CountryDisplay[]>(() => ["United States", "Canada"], []);

  const schoolYearOptions = useMemo<string[]>(() => getSchoolYearOptions(), []);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        email: Yup.string()
            .trim()
            .required("Email is required")
            .email("Enter a valid email address")
            // TLD capped at 18, the longest in the IANA root zone (.travelersinsurance), so
            // no real address is affected.  Catches addresses that ran into other text, such
            // as a name appended without a separator.  A short append still reads as a
            // plausible TLD and gets through; rejecting those needs the real TLD list.
            .matches(
                /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,18}$/,
                "Email must contain a valid domain (e.g. example@domain.com)"
            ),

        schoolName: Yup.string().trim().required("School name is required"),
        schoolYear: Yup.string()
          .trim()
          .required("School year is required")
          .oneOf(schoolYearOptions, "Select a school year from the list"),
        bannerName: Yup.string().trim().required("Banner template is required"),

        attention: Yup.string().trim().required("Attention is required"),
        address1: Yup.string().trim().required("Street address is required"),
        city: Yup.string().trim().required("City is required"),
        state: Yup.string().trim().required("State/Province is required"),
        zip: Yup.string().trim().required("Zip/Postal code is required"),

        country: Yup.mixed<CountryDisplay>()
          .oneOf(["United States", "Canada"] as const, "Country is required")
          .required("Country is required"),

        phone: Yup.string()
          .transform((val:any) => onlyDigits(val || ""))
          .required("Phone is required")
          .matches(/^\d{10}$/, "Phone must be exactly 10 digits"),
      }),
    [schoolYearOptions]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      email: "",
      schoolName: "",
      schoolYear: "",
      bannerName: "",

      attention: "",
      address1: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      phone: "",
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false, 
    onSubmit: async (values:any) => {
      try {
        setLoading(true);

        const postData: OrderData = {
          email: values.email.trim(),
          attention: values.attention.trim(),
          address1: values.address1.trim(),
          city: values.city.trim(),
          state: values.state.trim(),
          zip: values.zip.trim(),
          schoolName: values.schoolName.trim(),
          schoolYear: values.schoolYear.trim(),
          banner: values.bannerName.trim(),
          bannerOrderId: editOrderId,
          orderDate: "",
          country: values.country === "United States" ? "US" : "CA",
          phone: onlyDigits(values.phone).slice(0, 10),
          username: userName,
          orderGroup: "",
          trackingNumber: "",
        };

        const ok = await OrderPageService.submitOrder(postData);

        if (ok) {
          toast.success("Order saved successfully!");
          navigate(`${PUBLIC_PATH}/summary`, { replace: false });
        } else {
          toast.error("There was a problem submitting your order. Please try again later.");
        }
      } catch (err) {
        console.error(err);
        toast.error("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const showErrorToastIfAny = useCallback(() => {
    const keys = Object.keys(formik.errors);
    if (keys.length > 0) {
      toast.error("Please fix the highlighted fields.");
      return true;
    }
    return false;
  }, [formik.errors]);

  const onLoad = useCallback(async () => {
    localStorage.setItem("mode", "create");

    const orderId = searchParams.get("orderid");
    if (orderId) setEditOrderId(orderId);

    const localUserName = localStorage.getItem("username");
    const token = localStorage.getItem("token");

    if (!localUserName || !token) {
      navigate(`${PUBLIC_PATH}/login`, { replace: true });
      return;
    }

    try {
      const apiUsername: string = await OrderPageService.getUsernameByToken(token);

      if (localUserName !== apiUsername) {
        navigate(`${PUBLIC_PATH}/login`, { replace: true });
        return;
      }

      setUserName(apiUsername);

      if (orderId) {
        const userEmail = localStorage.getItem("email") || "";
        if (userEmail) {
          const defaultData: OrderData = await OrderPageService.getOrderInfo(userEmail);

          const countryDisplay: CountryDisplay = defaultData.country === "CA" ? "Canada" : "United States";
          setIsCountryUS(isUS(countryDisplay));

          formik.setValues({
            email: defaultData.email || "",
            schoolName: defaultData.schoolName || "",
            // Older orders can hold free text here, so only prefill an offered year.
            schoolYear: schoolYearOptions.includes(defaultData.schoolYear) ? defaultData.schoolYear : "",
            bannerName: defaultData.banner || "",

            attention: defaultData.attention || "",
            address1: defaultData.address1 || "",
            city: defaultData.city || "",
            state: defaultData.state || "",
            zip: defaultData.zip || "",
            country: countryDisplay,
            phone: onlyDigits(defaultData.phone || "").slice(0, 10),
          });
        }
      }
    } catch (err) {
      console.error("Order load error:", err);
      navigate(`${PUBLIC_PATH}/login`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams, schoolYearOptions]);

  useEffect(() => {
    void onLoad();
  }, [onLoad]);

  const fieldError = (name: keyof FormValues) =>
    formik.touched[name] && formik.errors[name] ? String(formik.errors[name]) : "";

  // py-2 keeps the inputs close in height to the react-dropdown controls they sit beside.
  const inputClass = (name: keyof FormValues) =>
    `w-full border px-3 py-2 text-gray-700 placeholder-gray-400 outline-none ${
      fieldError(name) ? "border-red-500" : "border-gray-200"
    }`;

  const halfInputClass = (name: keyof FormValues) =>
    `w-full border px-3 py-2 text-gray-700 placeholder-gray-400 outline-none ${
      fieldError(name) ? "border-red-500" : "border-gray-200"
    }`;

  const onCountryChange = (value: CountryDisplay) => {
    // Swap the state field first, so the validation setFieldValue triggers is the last to run.
    setIsCountryUS(isUS(value));
    formik.setFieldValue("country", value, true);
  };

  return (
    // Room below the card so an open dropdown near the bottom can be scrolled into view;
    // react-dropdown menus are 200px tall and would otherwise run off the end of the page.
    <div className="pb-56">
      <TopBanner />
      <ToastContainer position="top-right" autoClose={2500} />

      <div className="small-card relative pb-5 border-b border-gray-400 shadow-md">
        <div className="container">
          <div className="text-center text-2xl font-bold mt-5 mb-6">ORDER INFORMATION</div>

          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const errs = await formik.validateForm();
              formik.setTouched(
                {
                  email: true,
                  schoolName: true,
                  schoolYear: true,
                  bannerName: true,
                  attention: true,
                  address1: true,
                  city: true,
                  state: true,
                  zip: true,
                  country: true,
                  phone: true,
                },
                true
              );

              if (Object.keys(errs).length > 0) {
                showErrorToastIfAny();
                return;
              }

              formik.handleSubmit();
            }}
          >
            
            <div className="text-xl font-bold mb-3">Your Information</div>
            <div className="max-w-md">
              <input
                name="email"
                placeholder="CEC Email"
                className={inputClass("email")}
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {!!fieldError("email") && (
                <div className="text-red-600 text-sm mt-1">{fieldError("email")}</div>
              )}
            </div>

            <div className="text-xl font-bold mt-6 mb-3">Banner Information</div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  name="schoolName"
                  placeholder="Enter School Name"
                  className={halfInputClass("schoolName")}
                  value={formik.values.schoolName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {!!fieldError("schoolName") && (
                  <div className="text-red-600 text-sm mt-1">{fieldError("schoolName")}</div>
                )}
              </div>

              <div>
                <Dropdown
                  options={schoolYearOptions}
                  onChange={(opt: DropdownOption) => {
                    // Mark touched without validating, so the only validation run is the one
                    // setFieldValue triggers - it is the one that sees the chosen value.
                    formik.setFieldTouched("schoolYear", true, false);
                    formik.setFieldValue("schoolYear", String(opt.value), true);
                  }}
                  value={formik.values.schoolYear}
                  placeholder="School Year"
                />
                {!!fieldError("schoolYear") && (
                  <div className="text-red-600 text-sm mt-1">{fieldError("schoolYear")}</div>
                )}
              </div>
            </div>

            <div className="max-w-md mt-3">
              <Dropdown
                options={bannerOptions}
                onChange={(opt: DropdownOption) => {
                  formik.setFieldTouched("bannerName", true, false);
                  formik.setFieldValue("bannerName", String(opt.value), true);
                }}
                value={formik.values.bannerName}
                placeholder="Banner Template"
              />
              {!!fieldError("bannerName") && (
                <div className="text-red-600 text-sm mt-1">{fieldError("bannerName")}</div>
              )}
            </div>
            <div className="text-xl font-bold mt-6 mb-3">Shipping Information</div>
            <div className="space-y-3">
              <div>
                <input
                  name="attention"
                  placeholder="Attention"
                  className={inputClass("attention")}
                  value={formik.values.attention}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {!!fieldError("attention") && (
                  <div className="text-red-600 text-sm mt-1">{fieldError("attention")}</div>
                )}
              </div>

              <div>
                <input
                  name="address1"
                  placeholder="Street Address"
                  className={inputClass("address1")}
                  value={formik.values.address1}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {!!fieldError("address1") && (
                  <div className="text-red-600 text-sm mt-1">{fieldError("address1")}</div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    name="city"
                    placeholder="City"
                    className={halfInputClass("city")}
                    value={formik.values.city}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {!!fieldError("city") && (
                    <div className="text-red-600 text-sm mt-1">{fieldError("city")}</div>
                  )}
                </div>

                <div>
                  {isCountryUS ? (
                    <>
                      <Dropdown
                        options={stateOptions}
                        onChange={(opt: DropdownOption) => {
                          formik.setFieldTouched("state", true, false);
                          formik.setFieldValue("state", String(opt.value), true);
                        }}
                        value={formik.values.state}
                        placeholder="State"
                      />
                      {!!fieldError("state") && (
                        <div className="text-red-600 text-sm mt-1">{fieldError("state")}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        name="state"
                        placeholder="State/Province"
                        className={halfInputClass("state")}
                        value={formik.values.state}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {!!fieldError("state") && (
                        <div className="text-red-600 text-sm mt-1">{fieldError("state")}</div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    name="zip"
                    placeholder="Zip/Postal Code"
                    className={halfInputClass("zip")}
                    value={formik.values.zip}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {!!fieldError("zip") && (
                    <div className="text-red-600 text-sm mt-1">{fieldError("zip")}</div>
                  )}
                </div>

                <div>
                  <Dropdown
                    options={countryOptions}
                    onChange={(opt: DropdownOption) => {
                      const c = String(opt.value) as CountryDisplay;
                      formik.setFieldTouched("country", true, false);
                      onCountryChange(c);
                    }}
                    value={formik.values.country}
                    placeholder="Country"
                  />
                  {!!fieldError("country") && (
                    <div className="text-red-600 text-sm mt-1">{fieldError("country")}</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                <div>
                  <input
                    name="phone"
                    placeholder="Phone"
                    className={halfInputClass("phone")}
                    value={formik.values.phone}
                    onChange={(e) => {
                      const digits = onlyDigits(e.target.value).slice(0, 10);
                      formik.setFieldValue("phone", digits);
                    }}
                    onBlur={() => formik.setFieldTouched("phone", true, true)}
                  />
                  {!!fieldError("phone") && (
                    <div className="text-red-600 text-sm mt-1">{fieldError("phone")}</div>
                  )}
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-lime-500 hover:bg-lime-600 text-white font-semibold px-10 py-3 disabled:opacity-60"
                  >
                    {loading ? "Saving..." : "Review Order"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
